import type { CustomAnalyticsReportSummary } from "@redotech/merchant-sdk/rpc/schema/analytics/reports";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import {
  type InfiniteData,
  type QueryClient,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getReportTitle } from "../custom-analytics-report-page-utils";
import { getCustomAnalyticsReportsQueryInvalidationKey } from "./use-custom-analytics-reports-query";

const CUSTOM_ANALYTICS_REPORT_QUERY_KEY = "customAnalyticsReport";

export function getCustomAnalyticsReportQueryKey(
  teamId: string,
  customReportId: string,
) {
  return [CUSTOM_ANALYTICS_REPORT_QUERY_KEY, teamId, customReportId] as const;
}

export function getCustomAnalyticsReportQueryInvalidationKey(teamId: string) {
  return [CUSTOM_ANALYTICS_REPORT_QUERY_KEY, teamId] as const;
}

export function getCachedCustomAnalyticsReport({
  customReportId,
  queryClient,
  teamId,
}: {
  customReportId: string;
  queryClient: QueryClient;
  teamId: string;
}) {
  const reports = queryClient
    .getQueriesData<
      InfiniteData<{ reports: CustomAnalyticsReportSummary[] }>
    >({ queryKey: getCustomAnalyticsReportsQueryInvalidationKey(teamId) })
    .flatMap(([, data]) => data?.pages.flatMap((page) => page.reports) ?? []);
  const detail = queryClient.getQueryData<CustomAnalyticsReportSummary>(
    getCustomAnalyticsReportQueryKey(teamId, customReportId),
  );
  if (detail) {
    reports.push(detail);
  }
  return reports
    .filter((report) => report.id === customReportId)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())[0];
}

export function useCustomAnalyticsReportQuery({
  customReportId,
  enabled = true,
  includeColumns = true,
}: {
  customReportId: string;
  enabled?: boolean;
  includeColumns?: boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const queryClient = useQueryClient();
  const cachedReport = getCachedCustomAnalyticsReport({
    customReportId,
    queryClient,
    teamId: team._id,
  });

  const query = useQuery({
    queryKey: includeColumns
      ? getCustomAnalyticsReportQueryKey(team._id, customReportId)
      : [
          ...getCustomAnalyticsReportQueryKey(team._id, customReportId),
          "details",
        ],
    enabled,
    queryFn: async ({ signal }) => {
      return await rpcClient.getCustomAnalyticsReport(
        { id: customReportId, includeColumns },
        { signal },
      );
    },
    refetchInterval: (query) => {
      if (query.state.data?.status === "preparing") {
        return 2 * 1000;
      }
      if (query.state.data?.status === "ready") {
        // Ready reports only need occasional refreshes; source tables update on a slower cadence.
        return 5 * 60 * 1000;
      }
      return false;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  return {
    ...query,
    cachedTitle: cachedReport
      ? getReportTitle({ report: cachedReport })
      : undefined,
    cachedTimezone: cachedReport?.timezone,
  };
}
